import { useContext, useEffect, useReducer } from "react";
import "../../app/App.scss";
import Header from "../../features/common/elements/Header";
import { sendRequest } from "../../common/http";
import { useParams } from "react-router-dom";
import OrganizationContent from "./OrganizationContent";
import {
  InsightsUser,
  Institution,
  Organization,
  defaultOrganization,
} from "../common/interfaces/Interface";
import PssSideNav from "../common/elements/PssSideNav";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";
import Loader from "../common/elements/Loader";
import ApiError from "../common/elements/ApiError";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useIntl } from 'react-intl';

interface Action {
  type: string;
  organization: Organization;
  component: string;
}

const defaultAction: Action = {
  type: "",
  organization: defaultOrganization,
  component: "",
};
interface BodyState {
  organization: Organization;
  pssOrganization: Organization;
  showMainLoader: boolean;
  apiError: boolean;
}

const reducer = (state: BodyState, action: Action) => {
  switch (action.type) {
    case "setOrganization":
      return {
        ...state,
        organization: action.organization,
        showMainLoader: false,
      };
    case "setPssOrganization":
      return {
        ...state,
        pssOrganization: action.organization,
        showMainLoader: false,
      };
    case "setApiError":
      return {
        ...state,
        apiError: true,
        showMainLoader: false,
      };

    default:
      return state;
  }
};

interface UrlParams {
  institution_uuid: string;
  program_uuid: string;
}

const initialState: BodyState = {
  organization: defaultOrganization,
  pssOrganization: defaultOrganization,
  showMainLoader: true,
  apiError: false,
};

export interface OrgInsightsBodyProps {
  auth: Boolean | undefined;
  productView: "navigator" | "planning";
}
export function OrgInsightsBody({
  auth,
  productView,
}: OrgInsightsBodyProps): JSX.Element {
  return (
    <>
      {" "}
      {auth ? (
        <OrgInsights productView={productView} />
      ) : (
        <UnauthorizedComponent />
      )}{" "}
    </>
  );
}

interface OrgInsightsProps {
  productView: "navigator" | "planning";
}

function OrgInsights({ productView }: OrgInsightsProps): JSX.Element {
  const { institution, sysUser } = useContext<AppContextInterface>(AppContext);
  const intl = useIntl();

  const { institution_uuid: institutionUuid, program_uuid: programUuid } =
    useParams<UrlParams>();

  const [state, dispatch] = useReducer(reducer, initialState);

  const programName = state.organization?.name || state.pssOrganization?.name;
  const pageTitle = programName
    ? `${programName} - ${intl.formatMessage({
        id: "app.pageTitle.programInsights",
      })}`
    : intl.formatMessage({ id: "app.pageTitle.programInsights" });

  usePageTitle(pageTitle);

  useEffect(() => {
    if (institutionUuid && programUuid && institution) {
      const onError = () => {
        dispatch({
          ...defaultAction,
          type: "setApiError",
        });
      };

      if (productView === "navigator") {
        const applyResult = (result: Organization) => {
          dispatch({
            ...defaultAction,
            type: "setOrganization",
            organization: result,
          });
        };

        sendRequest(
          {
            uri: `/org_insights/institution/${institutionUuid}/program/${programUuid}/program_info`,
            camelize: true,
          },
          applyResult,
          onError,
        );
      } else if (productView === "planning") {
        const applyResult = (result: Organization) => {
          dispatch({
            ...defaultAction,
            type: "setPssOrganization",
            organization: result,
          });
        };

        sendRequest(
          {
            uri: `/org_insights/pss/organization/${programUuid}`,
            camelize: true,
          },
          applyResult,
          onError,
        );
      }
    }
  }, [institutionUuid, programUuid]);

  let platformUuid =
    (state.organization && state.organization.uuid) ||
    state.pssOrganization.platformUuid;

  return (
    <>
      {state.showMainLoader && <Loader />}
      {state.apiError ? (
        <ApiError />
      ) : (
        <>
          {institution && productView === "navigator" && (
            <NavigatorOrgInsightsView
              institution={institution}
              user={sysUser}
              organization={state.organization}
              platformUuid={platformUuid}
              productView={productView}
            />
          )}
          {institution && productView === "planning" && (
            <PlanningOrgInsightsView
              institution={institution}
              organization={state.organization}
              pssOrganization={state.pssOrganization}
              platformUuid={platformUuid}
              productView={productView}
            />
          )}
        </>
      )}
    </>
  );
}
interface OrgInsightsViewProps {
  institution: Institution | null;
  organization: Organization;
  pssOrganization: Organization;
  platformUuid: string | null;
  productView: "planning" | "navigator";
}
const PlanningOrgInsightsView = ({
  institution,
  organization,
  pssOrganization,
  platformUuid,
  productView,
}: OrgInsightsViewProps) => {
  let displayName =
    (organization && organization.name) ||
    pssOrganization.name ||
    (institution && institution.name);

  return (
    <div>
      {institution && institution.platformUuid != "" && (
        <>
            <main
              id="maincontent"
              className="main-body-container org-programs-wrapper"
            >
              <Header displayName={displayName} productView={productView} />
            <div className="insights-hub-body flex-row">
              <PssSideNav
                organization={organization}
                pssOrganization={pssOrganization}
              />
              <div id="main-org-profile-container" className="main-org-profile">
                <OrganizationContent platformUuid={platformUuid} />
              </div>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

interface NavigatorOrgInsightsViewProps {
  institution: Institution | null;
  user: InsightsUser | null;
  organization: Organization;
  platformUuid: string | null;
  productView: "planning" | "navigator";
}
const NavigatorOrgInsightsView = ({
  institution,
  user,
  organization,
  platformUuid,
  productView,
}: NavigatorOrgInsightsViewProps) => {
  let displayName =
    (organization && organization.name) || (institution && institution.name);

  return (
    <div>
      {institution && user && institution.platformUuid != "" && (
        <>
          {organization.platformUuid !== "" && (
            <Header displayName={displayName} productView={productView} />
          )}

          <div className="insights-hub-body flex-row">
            <div id="main-org-profile-container" className="main-org-profile">
              <OrganizationContent platformUuid={platformUuid} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
